import { File } from '@tribeplatform/gql-client/types'
import { useMedia } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { T } from '../i18n/components/T.js'

import { Slide } from './@types.js'
import { AudioVidePreviewRenderer } from './MediaPreviewRenderers/AudioVidePreviewRenderer.js'
import { ImagePreviewRenderer } from './MediaPreviewRenderers/ImagePreviewRenderer.js'
import { PdfPreviewRenderer } from './MediaPreviewRenderers/PdfPreviewRenderer.js'
import {
  fileHasPreview,
  isAudioExtension,
  isPdfExtension,
  isVideoExtension,
} from './utils.js'

type MediaPreviewProps = {
  isActive: boolean
  onDownload: () => void
  media: Slide & {
    description?: string
  }
}
export const MediaPreview = ({
  media,
  isActive,
  onDownload,
}: MediaPreviewProps) => {
  if ('deferred' in media) {
    const { id } = media
    return (
      <DeferredMediaPreview
        mediaId={id}
        isActive={isActive}
        onDownload={onDownload}
      />
    )
  }
  return (
    <MediaPreviewRenderer
      media={media}
      isActive={isActive}
      onDownload={onDownload}
    />
  )
}

type DeferredMediaPreviewProps = {
  mediaId: string
  isActive: boolean
  onDownload: () => void
}
const DeferredMediaPreview = ({
  mediaId,
  isActive,
  onDownload,
}: DeferredMediaPreviewProps) => {
  const { data: media, isInitialLoading } = useMedia({
    variables: {
      id: mediaId,
    },
    fields: {
      onFile: 'basic',
    },
    useQueryOptions: {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !!mediaId,
    },
  })

  if (isInitialLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <SvgIcon className="animate-spin h-7 w-7" name="spinner" />
      </div>
    )
  }

  if (!media) {
    return (
      <div className="h-full flex items-center justify-center">
        <T
          defaultMessage="Something went wrong and we were unable to fetch information of this file."
          id="MediaPreview.UnableToFetchMediaError"
        />
      </div>
    )
  }

  return (
    <MediaPreviewRenderer
      media={media as File}
      isActive={isActive}
      onDownload={onDownload}
    />
  )
}

export const MediaPreviewRenderer = ({
  media,
  isActive,
  onDownload,
}: MediaPreviewProps) => {
  const { name, url, description } = media

  /**
   * Inline images does not have extensions associated with them.
   * As result, we need to check if extension exists before passing media
   * to hasPreview. Otherwise, inline images won't be displayed in media modal.
   */
  const extension = (media as File)?.extension?.toLowerCase()
  if (extension && !fileHasPreview(media as File)) {
    return <UnsupportedExtension name={media.name} onDownload={onDownload} />
  }

  if (isPdfExtension(extension)) {
    return <PdfPreviewRenderer url={url} name={name} />
  }

  const isAudio = isAudioExtension(extension)
  const isVideo = isVideoExtension(extension)
  if (isAudio || isVideo) {
    return (
      <AudioVidePreviewRenderer
        url={url}
        isAudio={isAudio}
        isActive={isActive}
      />
    )
  }

  return (
    <ImagePreviewRenderer url={url} name={name} description={description} />
  )
}

const UnsupportedExtension = ({ name, onDownload }) => (
  <div className="flex flex-col h-full w-full items-center justify-center">
    <p>
      <T
        id="MediaModal.MediaPreview.NotSupportedText"
        defaultMessage="Preview not supported."
      />
    </p>
    <Button
      size="md"
      leadingIcon={<SvgIcon name="download" />}
      onClick={onDownload}
      variant="plain"
    >
      {name || <T id="Generics.Download" defaultMessage="Download" />}
    </Button>
  </div>
)
